import React from 'react'
import {
  Box,
  Button,
  Chip,
  Paper,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import type { PHIPrediction } from '../../../types/phi'

interface EditingControlsProps {
  isEditMode: boolean
  currentEditingBox: { sopInstanceUid: string; index: number } | null
  currentPrediction: PHIPrediction | null
  onActionChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  onScopeChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  onSaveCurrentEdits: () => Promise<void>
  onCancelEditing: () => void
}

export function EditingControls({
  isEditMode,
  currentEditingBox,
  currentPrediction,
  onActionChange,
  onScopeChange,
  onSaveCurrentEdits,
  onCancelEditing,
}: EditingControlsProps) {
  // Show general edit mode indicator when no specific box is selected
  if (isEditMode && !currentEditingBox) {
    return (
      <Box
        mt={2}
        sx={{
          backgroundColor: 'rgba(90, 204, 230, 0.1)',
          borderRadius: '4px',
          padding: '8px 16px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <EditIcon sx={{ mr: 1, color: 'info.main' }} />
        <Typography color="info.main">Edit mode active - Click a PHI box to modify its properties</Typography>
      </Box>
    )
  }

  // Show editing controls only when a specific box is selected
  if (!isEditMode || !currentEditingBox || !currentPrediction) {
    return null
  }

  return (
    <Paper
      sx={{
        mt: 2,
        mb: 2,
        p: 2,
        backgroundColor: 'rgba(90, 204, 230, 0.1)',
        borderRadius: '4px',
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Typography variant="subtitle1" color="primary" fontWeight="bold" mb={1}>
          Editing PHI Box
        </Typography>
        <Button size="small" variant="text" color="inherit" onClick={onCancelEditing} startIcon={<CloseIcon />}>
          Cancel
        </Button>
      </Box>

      <Box display="flex" flexWrap="wrap" gap={4}>
        <FormControl component="fieldset">
          <FormLabel component="legend">Action</FormLabel>
          <RadioGroup row value={currentPrediction.action || 'apply'} onChange={onActionChange}>
            <FormControlLabel value="apply" control={<Radio size="small" />} label="Apply" />
            <FormControlLabel value="remove" control={<Radio size="small" />} label="Remove" />
          </RadioGroup>
        </FormControl>

        <FormControl component="fieldset">
          <FormLabel component="legend">Scope</FormLabel>
          <RadioGroup row value={currentPrediction.scope || 'single'} onChange={onScopeChange}>
            <FormControlLabel value="single" control={<Radio size="small" />} label="Single" />
            <FormControlLabel value="all" control={<Radio size="small" />} label="All" />
          </RadioGroup>
        </FormControl>

        <Box ml="auto" display="flex" alignItems="flex-end">
          <Chip
            label={`Source: ${currentPrediction.source || 'AI'}`}
            size="small"
            color={currentPrediction.source === 'USER' ? 'primary' : 'warning'}
            sx={{ mr: 2 }}
          />
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={onSaveCurrentEdits}
            startIcon={<SaveIcon />}
          >
            Save Changes
          </Button>
        </Box>
      </Box>
    </Paper>
  )
}
